import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/api';
import { Send, MessageCircle, User, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function ThreadList({ threads, selected, onSelect }) {
  return (
    <div className="w-72 flex-shrink-0 border-r border-[#2D3748] flex flex-col">
      <div className="p-4 border-b border-[#2D3748]">
        <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#7B8EA2]">// ALL CONVERSATIONS</p>
      </div>
      <div className="flex-1 overflow-y-auto">
        {threads.length === 0 ? (
          <div className="p-6 text-center">
            <MessageCircle size={24} className="mx-auto mb-2 text-[#2D3748]" />
            <p className="text-[#7B8EA2] font-['IBM_Plex_Sans'] text-sm">No conversations yet</p>
          </div>
        ) : threads.map(t => (
          <button key={t.id} onClick={() => onSelect(t)}
            className={`w-full p-4 text-left border-b border-[#2D3748]/40 hover:bg-[#232B35] transition-colors ${selected?.id === t.id ? 'bg-[#232B35] border-l-2 border-l-[#3B82F6]' : ''}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-white text-sm font-['Manrope'] font-bold truncate">
                  {t.guest_name || t.guest_email || 'Client'}
                </p>
                {t.guest_email && <p className="text-[#7B8EA2] text-[10px] font-['JetBrains_Mono'] truncate">{t.guest_email}</p>}
                {t.last_message && <p className="text-[#C8D4E0] text-xs font-['IBM_Plex_Sans'] truncate mt-0.5">{t.last_message}</p>}
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                {t.unread_ops > 0 && (
                  <span className="w-5 h-5 rounded-full bg-[#3B82F6] text-white text-[10px] font-bold flex items-center justify-center font-['JetBrains_Mono']">
                    {t.unread_ops}
                  </span>
                )}
                <span className="text-[10px] text-[#7B8EA2] font-['JetBrains_Mono']">
                  {t.last_message_at ? new Date(t.last_message_at).toLocaleDateString() : ''}
                </span>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Chat() {
  const { user, token } = useAuth();
  const [threads, setThreads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [wsReady, setWsReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const wsRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => { loadThreads(); }, []);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const loadThreads = async () => {
    setLoading(true);
    try {
      const r = await api.get('/chat/threads');
      setThreads(r.data);
    } catch {}
    finally { setLoading(false); }
  };

  const selectThread = async (t) => {
    setSelected(t);
    const msgs = await api.get(`/chat/threads/${t.id}/messages`);
    setMessages(msgs.data);
    connectWs(t.id);
    // Mark as read
    setThreads(prev => prev.map(th => th.id === t.id ? { ...th, unread_ops: 0 } : th));
  };

  const connectWs = (tid) => {
    if (wsRef.current) wsRef.current.close();
    const wsBase = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsBase}/api/chat/ws/${tid}?token=${token}`);
    ws.onopen = () => setWsReady(true);
    ws.onclose = () => setWsReady(false);
    ws.onmessage = (e) => {
      const msg = JSON.parse(e.data);
      setMessages(prev => [...prev, msg]);
    };
    wsRef.current = ws;
  };

  const sendMessage = () => {
    if (!input.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ content: input }));
    setInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  return (
    <div className="h-[calc(100vh-0px)] flex flex-col" data-testid="chat-page">
      <div className="flex items-center justify-between px-6 py-4 border-b border-[#2D3748]/60 flex-shrink-0">
        <div>
          <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#7B8EA2] mb-1">// MESSAGING CENTER</p>
          <h1 className="text-white font-['Manrope'] font-extrabold text-2xl">Live Chat</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[#22C55E] live-dot" />
          <span className="text-[#22C55E] text-xs font-['JetBrains_Mono']">{threads.length} conversations</span>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        <ThreadList threads={threads} selected={selected} onSelect={selectThread} />

        {selected ? (
          <div className="flex-1 flex flex-col">
            {/* Thread header */}
            <div className="px-6 py-3 border-b border-[#2D3748] bg-[#1A2028] flex items-center gap-3 flex-shrink-0">
              <div className="w-9 h-9 rounded-sm bg-[#3B82F6]/10 border border-[#3B82F6]/25 flex items-center justify-center">
                <User size={16} className="text-[#3B82F6]" />
              </div>
              <div>
                <p className="text-white text-sm font-['Manrope'] font-bold">{selected.guest_name || 'Client'}</p>
                {selected.guest_email && <p className="text-[#7B8EA2] text-xs font-['JetBrains_Mono']">{selected.guest_email}</p>}
              </div>
              <div className="ml-auto flex items-center gap-2">
                {wsReady ? (
                  <span className="text-[#22C55E] text-[10px] font-['JetBrains_Mono'] flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#22C55E]" /> Connected
                  </span>
                ) : (
                  <span className="text-[#7B8EA2] text-[10px] font-['JetBrains_Mono']">Connecting...</span>
                )}
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-[#0F1419]">
              {messages.length === 0 && (
                <div className="text-center py-12">
                  <MessageCircle size={28} className="mx-auto mb-3 text-[#2D3748]" />
                  <p className="text-[#7B8EA2] font-['IBM_Plex_Sans'] text-sm">No messages yet. Say hello!</p>
                </div>
              )}
              {messages.map(m => {
                const isMine = m.sender_role !== 'guest' && m.sender_role !== 'customer';
                return (
                  <div key={m.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[70%] px-4 py-3 rounded-sm ${isMine ? 'bg-[#3B82F6] text-white' : 'bg-[#242D38] border border-[#2D3748] text-white'}`}>
                      {!isMine && <p className="text-[10px] font-bold mb-1 font-['Manrope'] text-[#3B82F6]">{m.sender_name}</p>}
                      <p className="text-sm font-['IBM_Plex_Sans']">{m.content}</p>
                      <p className={`text-[10px] mt-1.5 ${isMine ? 'text-white/50' : 'text-[#7B8EA2]'}`}>
                        {m.sender_name} · {m.created_at ? new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-[#2D3748] bg-[#1A2028] flex gap-3">
              <textarea value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown}
                rows={2} placeholder="Type your reply... (Enter to send)"
                className="flex-1 px-4 py-2.5 bg-[#0A0D10] border border-[#2D3748] rounded-sm text-white font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6] transition-colors resize-none"
                data-testid="ops-chat-input" />
              <button onClick={sendMessage} disabled={!input.trim() || !wsReady} data-testid="ops-chat-send"
                className="px-4 bg-[#3B82F6] text-white rounded-sm hover:bg-[#2563EB] disabled:opacity-40 transition-colors flex items-center gap-2 font-['Manrope'] font-bold text-sm">
                <Send size={15} /> Send
              </button>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-[#0F1419]">
            <div className="text-center">
              <MessageCircle size={40} className="mx-auto mb-4 text-[#2D3748]" />
              <p className="text-[#7B8EA2] font-['IBM_Plex_Sans']">Select a conversation to start replying</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
